import React, {useCallback, useEffect, useRef, useState} from "react";
import styled from "styled-components";
import {toast, ToastContainer} from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {signInWithEmailAndPassword, signOut} from 'firebase/auth';
import {auth, functions} from "../../../firebase";
import {GLOBAL_BORDER_RADIUS, HIGHLIGHT_COLOR} from "../../../costants";
import {globalElementInputStyle} from "../../../global/style/globalElementInputStyle";
import {httpsCallable} from "firebase/functions";
import MyNormalText from "../../commons/myNormalText";
import {validateAndSetPassword} from "../../../global/password";
import Snackbar from "../../commons/snackBar";
import Loader from "../../commons/loader";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import {fromMomentDate, toMomentDate} from "../../../global/dates";
import MainButton from "../../commons/mainButton";
import useMyEscapeListener from "../../../global/listeners/useMyEscapeListener";
import useMyClickOutListener from "../../../global/listeners/useMyClickOutListener";

// Componente per la sezione Informazioni Generali
const GeneralInfoSection = ({generalInfosV2}) => {
    return (
        <Section>
            <div style={{marginBottom: 10}}>
                <MyNormalText text="Informazioni Generali" bold fontSize={18}/>
            </div>
            {["italian"].map((lang) => (
                <SubSection key={lang}>
                    {generalInfosV2[lang]?.length > 0 ? (
                        generalInfosV2[lang].map((info, index) => (
                            <MyNormalText key={index} text={`- ${info.info}`} multiline/>
                        ))
                    ) : (
                        <MyNormalText text="-"/>
                    )}
                </SubSection>
            ))}
        </Section>
    );
};

// Componente per il popup di modifica degli orari
const OpeningHoursPopup = ({
                               openingHoursFormData,
                               handleIsOpenChange,
                               handleOpeningHoursChange,
                               addHourSlot,
                               removeHourSlot,
                               handleSaveOpeningHours,
                               handleClose,
                               loadingUpdateUser,
                               primaryColor,
                               secondaryColor,
                           }) => {
    const dayNames = {
        1: "Lunedì",
        2: "Martedì",
        3: "Mercoledì",
        4: "Giovedì",
        5: "Venerdì",
        6: "Sabato",
        7: "Domenica",
    };

    const popupRef = useRef();
    useMyEscapeListener(handleClose);
    useMyClickOutListener(popupRef, handleClose);

    return (
        <PopupOverlay>
            <PopupContent ref={popupRef} backgroundColor={primaryColor}>
                <CloseButton onClick={handleClose} aria-label="Chiudi popup modifica orari">
                    ✕
                </CloseButton>
                <MyNormalText text="Modifica Orari di Apertura" bold fontSize={18}/>
                {openingHoursFormData.regularOpeningHours.map((day, dayIndex) => (
                    <div key={day.dayOfWeek} style={{marginBottom: 10}}>
                        <FlexRow>
                            <MyNormalText text={dayNames[day.dayOfWeek]}/>
                            <input
                                type="checkbox"
                                checked={day.isOpen}
                                onChange={(e) => handleIsOpenChange(dayIndex, e.target.checked)}
                                aria-label={`Aperto/Chiuso per ${dayNames[day.dayOfWeek]}`}
                            />
                            <MyNormalText text={day.isOpen ? 'Aperto' : 'Chiuso'}/>
                        </FlexRow>
                        {day.isOpen && (
                            <div style={{marginTop: 5}}>
                                {day.hours.map((slot, hourIndex) => (
                                    <FlexColumn key={hourIndex}>
                                        <input
                                            style={globalElementInputStyle.customInput}
                                            type="time"
                                            value={slot.open}
                                            onChange={(e) => handleOpeningHoursChange(dayIndex, hourIndex, 'open', e.target.value)}
                                            aria-label={`Orario di apertura per ${dayNames[day.dayOfWeek]}`}
                                        />
                                        <input
                                            style={globalElementInputStyle.customInput}
                                            type="time"
                                            value={slot.close}
                                            onChange={(e) => handleOpeningHoursChange(dayIndex, hourIndex, 'close', e.target.value)}
                                            aria-label={`Orario di chiusura per ${dayNames[day.dayOfWeek]}`}
                                        />
                                        {day.hours.length > 1 && (
                                            <div style={{marginBottom: 20}}>
                                                <MainButton
                                                    text="Rimuovi"
                                                    action={() => removeHourSlot(dayIndex, hourIndex)}
                                                    fontSize={14}
                                                    backgroundColor={HIGHLIGHT_COLOR}
                                                    height={25}
                                                    aria-label={`Rimuovi slot orario per ${dayNames[day.dayOfWeek]}`}
                                                />
                                            </div>
                                        )}
                                    </FlexColumn>
                                ))}
                                <div style={{marginTop: 5}}>
                                    <MainButton
                                        text="+"
                                        action={() => addHourSlot(dayIndex)}
                                        backgroundColor={secondaryColor}
                                        height={25}
                                        aria-label={`Aggiungi slot orario per ${dayNames[day.dayOfWeek]}`}
                                    />
                                </div>
                            </div>
                        )}
                    </div>
                ))}
                <MainButton
                    text="Salva Orari"
                    action={handleSaveOpeningHours}
                    disabled={loadingUpdateUser}
                    loading={loadingUpdateUser}
                    backgroundColor={secondaryColor}
                    aria-label="Salva orari di apertura"
                />
            </PopupContent>
        </PopupOverlay>
    );
};

// Componente per la sezione Orari di Apertura
const OpeningHoursSection = ({
                                 openingHoursV2,
                                 toggleOpeningHoursForm,
                                 secondaryColor,
                             }) => {
    const dayNames = {
        1: "Lunedì",
        2: "Martedì",
        3: "Mercoledì",
        4: "Giovedì",
        5: "Venerdì",
        6: "Sabato",
        7: "Domenica",
    };

    const formatHours = (day) => {
        if (!day.isOpen || day.hours.length === 0) {
            return 'Chiuso';
        }
        return day.hours.map((slot) => `${slot.open} - ${slot.close}`).join(', ');
    };

    return (
        <Section>
            <div style={{marginBottom: 10}}>
                <MyNormalText text="Orari di Apertura" bold fontSize={18}/>
            </div>
            {openingHoursV2.regularOpeningHours.map((day) => (
                <FlexRow key={day.dayOfWeek}>
                    <MyNormalText text={`- ${dayNames[day.dayOfWeek]}: `}/>
                    <MyNormalText text={formatHours(day)}/>
                </FlexRow>
            ))}
            <div style={{width: 200}}>
                <MainButton
                    text="Modifica"
                    action={toggleOpeningHoursForm}
                    backgroundColor={secondaryColor}
                    height={30}
                    aria-label="Modifica orari di apertura"
                />
            </div>
        </Section>
    );
};

// Componente principale
function Profile({rentUser, reloadUser, switchProfile, premiumPalette}) {
    const refund = rentUser.refund;
    const rentInfo = rentUser.rentInfo;
    const openingHoursV2 = rentUser.openingHoursV2 || {
        regularOpeningHours: [
            {dayOfWeek: "1", isOpen: false, hours: []},
            {dayOfWeek: "2", isOpen: false, hours: []},
            {dayOfWeek: "3", isOpen: false, hours: []},
            {dayOfWeek: "4", isOpen: false, hours: []},
            {dayOfWeek: "5", isOpen: false, hours: []},
            {dayOfWeek: "6", isOpen: false, hours: []},
            {dayOfWeek: "7", isOpen: false, hours: []},
        ],
    };
    const generalInfosV2 = rentUser.generalInfosV2 || {
        italian: [],
        english: [],
        german: [],
    };

    const primaryColor = premiumPalette.primaryColor;
    const secondaryColor = premiumPalette.secondaryColor;
    const primaryColorFirstAlternative = premiumPalette.primaryColorFirstAlternative;

    const [discounts, setDiscounts] = useState([]);
    const [closedDates, setClosedDates] = useState([]);
    const [closedFromDate, setClosedFromDate] = useState(null);
    const [closedToDate, setClosedToDate] = useState(null);
    const [newPassword, setNewPassword] = useState("");
    const [newPasswordValidated, setNewPasswordValidated] = useState(false);
    const [loadingUpdateUser, setLoadingUpdateUser] = useState(false);
    const [loadingChangePassword, setChangePassword] = useState(false);
    const [loadingChangeProfile, setLoadingChangeProfile] = useState(false);
    const [loadingClosedDate, setLoadingClosedDate] = useState(false);
    const [showOpeningHoursForm, setShowOpeningHoursForm] = useState(false);
    const [openingHoursFormData, setOpeningHoursFormData] = useState(null);

    useEffect(() => {
        const daysOfWeek = ["1", "2", "3", "4", "5", "6", "7"];
        const initialFormData = {
            regularOpeningHours: daysOfWeek.map((day) => {
                const existingDay = openingHoursV2.regularOpeningHours.find((d) => d.dayOfWeek === day) || {
                    dayOfWeek: day,
                    isOpen: false,
                    hours: [],
                };
                return {
                    dayOfWeek: day,
                    isOpen: existingDay.isOpen,
                    hours: existingDay.hours.length > 0 ? existingDay.hours : [],
                };
            }),
        };
        setOpeningHoursFormData(initialFormData);
    }, [openingHoursV2]);

    const logout = useCallback(async () => {
        try {
            await signOut(auth);
            toast.success("Logout effettuato con successo");
        } catch (error) {
            toast.error("Errore durante il logout");
        }
    }, []);

    const fetchDiscounts = useCallback(async () => {
        try {
            const getRentAppDiscounts = httpsCallable(functions, 'getRentAppDiscounts');
            const response = await getRentAppDiscounts({rentId: rentUser.rentId});
            setDiscounts(response.data.discounts);
        } catch (error) {
            toast.error("Errore nel caricamento degli sconti");
        }
    }, [rentUser.rentId]);

    const fetchUserClosedDates = useCallback(async () => {
        setLoadingClosedDate(true);
        try {
            const rentUserClosedDatesView = httpsCallable(functions, 'rentUserClosedDatesView');
            const response = await rentUserClosedDatesView({rentId: rentUser.rentId});
            setClosedDates(response.data.closedDates);
        } catch (error) {
            toast.error("Errore nel caricamento delle date di chiusura");
        } finally {
            setLoadingClosedDate(false);
        }
    }, [rentUser.rentId]);

    const handleOpenCloseRent = useCallback(async () => {
        if (!closedFromDate || !closedToDate) {
            toast.error("Seleziona entrambe le date di chiusura");
            return;
        }
        setLoadingUpdateUser(true);
        try {
            const rentUpdateClosedDatesCommand = httpsCallable(functions, 'rentUpdateClosedDatesCommand');
            const request = {
                rentId: rentUser.rentId,
                from: fromMomentDate(toMomentDate(closedFromDate)),
                to: fromMomentDate(toMomentDate(closedToDate)),
            };
            await rentUpdateClosedDatesCommand(request);
            toast.success("Date di chiusura aggiornate");
            fetchUserClosedDates();
        } catch (error) {
            toast.error("Errore nell'aggiornamento delle date di chiusura");
        } finally {
            setLoadingUpdateUser(false);
        }
    }, [rentUser.rentId, closedFromDate, closedToDate, fetchUserClosedDates]);

    const handleChangePassword = useCallback(async () => {
        if (!newPasswordValidated) {
            toast.error("Attenzione! La password deve essere lunga almeno 6 caratteri.");
            return;
        }
        setChangePassword(true);
        try {
            const changePassword = httpsCallable(functions, 'updateUser');
            const result = await changePassword({user: {uid: rentUser.rentId, password: newPassword}});
            if (result.data.message === 'OK') {
                await signInWithEmailAndPassword(auth, rentUser.email, newPassword);
                setNewPassword("");
                toast.success("Password cambiata con successo");
            } else {
                toast.error("Errore nel cambio della password");
            }
        } catch (error) {
            toast.error("Errore nel cambio della password");
        } finally {
            setChangePassword(false);
        }
    }, [rentUser.rentId, rentUser.email, newPassword, newPasswordValidated]);

    const handleSwitchProfile = useCallback(
        async (rentIdTarget) => {
            if (rentIdTarget === rentUser.rentId) return;
            setLoadingChangeProfile(true);
            try {
                await switchProfile(rentIdTarget);
                toast.success("Profilo cambiato con successo");
            } catch (error) {
                toast.error("Errore nel cambio del profilo");
            } finally {
                setLoadingChangeProfile(false);
            }
        },
        [rentUser.rentId, switchProfile]
    );

    const handleSelectClosedFromDate = useCallback((date) => {
        setClosedFromDate(date);
        setClosedToDate(date);
    }, []);

    const handleSelectClosedToDate = useCallback((date) => {
        if (toMomentDate(date).isBefore(toMomentDate(closedFromDate))) {
            setClosedFromDate(date);
            setClosedToDate(date);
        } else if (closedFromDate) {
            setClosedToDate(date);
        } else {
            setClosedFromDate(date);
            setClosedToDate(date);
        }
    }, [closedFromDate]);

    const handleDeleteClosedDate = useCallback(
        async (closedDate) => {
            setLoadingClosedDate(true);
            try {
                const rentRemoveClosedDateCommand = httpsCallable(functions, 'rentRemoveClosedDateCommand');
                const request = {
                    rentId: rentUser.rentId,
                    closedDateId: closedDate.closedDateId,
                };
                await rentRemoveClosedDateCommand(request);
                toast.success("Data di chiusura rimossa");
                fetchUserClosedDates();
            } catch (error) {
                toast.error("Errore nella rimozione della data di chiusura");
            } finally {
                setLoadingClosedDate(false);
            }
        },
        [rentUser.rentId, fetchUserClosedDates]
    );

    const handleInsertPassword = useCallback((e) => {
        validateAndSetPassword(e.target.value, setNewPassword, setNewPasswordValidated);
    }, []);

    const toggleOpeningHoursForm = useCallback(() => {
        setShowOpeningHoursForm((prev) => !prev);
    }, []);

    const handleOpeningHoursChange = useCallback((dayIndex, hourIndex, field, value) => {
        setOpeningHoursFormData((prev) => {
            const newData = {...prev};
            newData.regularOpeningHours[dayIndex].hours[hourIndex][field] = value;
            return newData;
        });
    }, []);

    const handleIsOpenChange = useCallback((dayIndex, isOpen) => {
        setOpeningHoursFormData((prev) => {
            const newData = {...prev};
            newData.regularOpeningHours[dayIndex].isOpen = isOpen;
            if (!isOpen) {
                newData.regularOpeningHours[dayIndex].hours = [];
            } else if (newData.regularOpeningHours[dayIndex].hours.length === 0) {
                newData.regularOpeningHours[dayIndex].hours = [{open: "09:00", close: "18:00"}];
            }
            return newData;
        });
    }, []);

    const addHourSlot = useCallback((dayIndex) => {
        setOpeningHoursFormData((prev) => {
            const newData = {...prev};
            newData.regularOpeningHours[dayIndex].hours.push({open: "09:00", close: "18:00"});
            return newData;
        });
    }, []);

    const removeHourSlot = useCallback((dayIndex, hourIndex) => {
        setOpeningHoursFormData((prev) => {
            const newData = {...prev};
            newData.regularOpeningHours[dayIndex].hours = newData.regularOpeningHours[dayIndex].hours.filter(
                (_, index) => index !== hourIndex
            );
            return newData;
        });
    }, []);

    const handleSaveOpeningHours = useCallback(async () => {
        if (!auth.currentUser) {
            toast.error("Devi essere autenticato per aggiornare gli orari");
            return;
        }
        setLoadingUpdateUser(true);
        try {
            const rentUpdateRentInfoCommand = httpsCallable(functions, 'rentUpdateRentInfoCommand');
            const request = {
                rentId: rentUser.rentId,
                fields: {
                    openingHoursV2: {
                        regularOpeningHours: openingHoursFormData.regularOpeningHours,
                    },
                },
            };
            const result = await rentUpdateRentInfoCommand(request);
            if (result.data.status === 'success') {
                setShowOpeningHoursForm(false);
                await reloadUser();
                toast.success("Orari salvati con successo");
            } else {
                toast.error("Errore durante il salvataggio degli orari");
            }
        } catch (error) {
            toast.error("Errore durante il salvataggio degli orari");
        } finally {
            setLoadingUpdateUser(false);
        }
    }, [rentUser.rentId, openingHoursFormData, reloadUser]);

    const getClosedDates = useCallback(() => {
        if (closedDates.length === 0) {
            return <MyNormalText text="-"/>;
        }
        return closedDates.map((closedDate, index) => (
            <FlexRow key={index}>
                <MyNormalText text="●"/>
                <MyNormalText text={closedDate.closedDates.from}/>
                <MyNormalText text="-"/>
                <MyNormalText text={closedDate.closedDates.to}/>
                <div style={{width: 80}}>
                    <MainButton
                        text="Cancella"
                        action={() => handleDeleteClosedDate(closedDate)}
                        fontSize={14}
                        disabled={loadingClosedDate}
                        backgroundColor={HIGHLIGHT_COLOR}
                        height={25}
                        aria-label={`Cancella data di chiusura ${closedDate.closedDates.from} - ${closedDate.closedDates.to}`}
                    />
                </div>
            </FlexRow>
        ));
    }, [closedDates, loadingClosedDate, handleDeleteClosedDate]);

    useEffect(() => {
        fetchDiscounts();
        fetchUserClosedDates();
    }, [fetchDiscounts, fetchUserClosedDates]);

    return (
        <Container>
            <ToastContainer position="top-right" autoClose={3000}/>
            {loadingChangeProfile && <Loader absoluteFullPage/>}
            <Section>
                <SubSection>
                    <MyNormalText text="Negozio" bold fontSize={18}/>
                    <MyNormalText text={rentUser.email}/>
                    <MyNormalText text={`${rentInfo.city}, ${rentInfo.street}`}/>
                </SubSection>
            </Section>
            {rentUser.contacts && (
                <Section>
                    <SubSection>
                        <MyNormalText text="Contatti" bold fontSize={18}/>
                        {rentUser.contacts.email && <MyNormalText text={`Email: ${rentUser.contacts.email}`}/>}
                        {rentUser.contacts.phoneNumber &&
                            <MyNormalText text={`Telefono: ${rentUser.contacts.phoneNumber}`}/>}
                    </SubSection>
                </Section>
            )}
            <GeneralInfoSection generalInfosV2={generalInfosV2}/>
            <OpeningHoursSection
                openingHoursV2={openingHoursV2}
                toggleOpeningHoursForm={toggleOpeningHoursForm}
                secondaryColor={secondaryColor}
            />
            {showOpeningHoursForm && (
                <OpeningHoursPopup
                    openingHoursFormData={openingHoursFormData}
                    handleIsOpenChange={handleIsOpenChange}
                    handleOpeningHoursChange={handleOpeningHoursChange}
                    addHourSlot={addHourSlot}
                    removeHourSlot={removeHourSlot}
                    handleSaveOpeningHours={handleSaveOpeningHours}
                    handleClose={toggleOpeningHoursForm}
                    loadingUpdateUser={loadingUpdateUser}
                    primaryColor={primaryColor}
                    secondaryColor={secondaryColor}
                />
            )}
            <Section>
                <SubSection>
                    <MyNormalText text="Info rimborso/annullo" bold fontSize={18}/>
                    <MyNormalText
                        text={`I clienti potranno richiedere un rimborso fino a ${refund.withinDays} gg prima della prenotazione`}
                    />
                    <MyNormalText text={`Trattieni il ${refund.fee}% del totale`}/>
                </SubSection>
            </Section>
            <Section>
                <SubSection>
                    <MyNormalText text="Lista sconti" bold fontSize={18}/>
                    <FlexRow wrap>
                        {discounts.length === 0 ? (
                            <MyNormalText text="Nessun codice sconto creato."/>
                        ) : (
                            discounts.map((discount, index) => (
                                <DiscountContainer key={index} backgroundColor={primaryColor}>
                                    <MyNormalText text={`${discount.code} (-${discount.percentageToApply}%)`}/>
                                    <MyNormalText text={discount.isAvailable ? 'Attivo' : 'Disattivo'}/>
                                </DiscountContainer>
                            ))
                        )}
                    </FlexRow>
                </SubSection>
            </Section>
            <ActionSection backgroundColor={primaryColor}>
                {loadingClosedDate && (
                    <LoaderWrapper>
                        <Loader isContained/>
                    </LoaderWrapper>
                )}
                <SubSection>
                    <MyNormalText text="Chiusura negozio" bold fontSize={18}/>
                    <MyNormalText text="Queste le date di chiusura"/>
                    <FlexColumn>{getClosedDates()}</FlexColumn>
                </SubSection>
                <SubSection>
                    <MyNormalText text="Inserisci nuovo"/>
                    <FlexRow wrap>
                        <StyledDatePicker
                            selected={closedFromDate}
                            onChange={handleSelectClosedFromDate}
                            showMonthYearDropdown={false}
                            dateFormat="dd/MM/yyyy"
                            locale="it"
                            placeholderText="Data inizio"
                            aria-label="Seleziona data di inizio chiusura"
                        />
                        <StyledDatePicker
                            selected={closedToDate}
                            onChange={handleSelectClosedToDate}
                            showMonthYearDropdown={false}
                            dateFormat="dd/MM/yyyy"
                            locale="it"
                            placeholderText="Data fine"
                            aria-label="Seleziona data di fine chiusura"
                        />
                    </FlexRow>
                </SubSection>
                <MainButton
                    text="Salva"
                    action={handleOpenCloseRent}
                    disabled={loadingUpdateUser}
                    loading={loadingUpdateUser}
                    backgroundColor={secondaryColor}
                    aria-label="Salva date di chiusura"
                />
            </ActionSection>
            <ActionSection backgroundColor={primaryColor}>
                <SubSection>
                    <MyNormalText text="Cambia password" bold fontSize={18}/>
                    <MyNormalText text="Deve essere lunga almeno 6 caratteri"/>
                </SubSection>
                <input
                    style={globalElementInputStyle.customInput}
                    type="password"
                    value={newPassword}
                    placeholder="Nuova password..."
                    onChange={handleInsertPassword}
                    aria-label="Inserisci nuova password"
                />
                <MainButton
                    text="Cambia password"
                    loading={loadingChangePassword}
                    backgroundColor={secondaryColor}
                    action={handleChangePassword}
                    aria-label="Cambia password"
                />
            </ActionSection>
            <ActionSection backgroundColor={primaryColor}>
                <SubSection>
                    <MyNormalText text="Cambia account" bold fontSize={18}/>
                    <MyNormalText text="Accedi ad un altro account collegato"/>
                </SubSection>
                <FlexRow wrap>
                    {rentUser.isMultiStoreRent ? (
                        rentUser.linkedStores.map((linkedStore) => (
                            <MainButton
                                key={linkedStore.rentId}
                                text={`${linkedStore.name} (${linkedStore.email})`}
                                action={() => handleSwitchProfile(linkedStore.rentId)}
                                backgroundColor={
                                    linkedStore.rentId === rentUser.rentId ? secondaryColor : primaryColorFirstAlternative
                                }
                                aria-label={`Cambia a profilo ${linkedStore.name}`}
                            />
                        ))
                    ) : (
                        <MyNormalText text="-"/>
                    )}
                </FlexRow>
            </ActionSection>
            <MainButton
                text="Logout"
                action={logout}
                textColor="white"
                backgroundColor="black"
                aria-label="Effettua logout"
            />
        </Container>
    );
}

// Stili con styled-components
const Container = styled.div`
    display: flex;
    flex-direction: column;
    align-self: flex-start;
    gap: 40px;
    padding: 20px;
    max-width: 600px;
    margin: 0 auto
`;

const Section = styled.section`
    display: flex;
    flex-direction: column;
    padding-left: 10px;
    border-left: 1px solid black;
`;

const SubSection = styled.div`
    display: flex;
    flex-direction: column;
    gap: 10px;
`;

const ActionSection = styled.div`
    position: relative;
    display: flex;
    flex-direction: column;
    padding: 20px;
    gap: 15px;
    border-radius: ${GLOBAL_BORDER_RADIUS}px;
    background-color: ${({backgroundColor}) => backgroundColor};
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
`;

const FlexRow = styled.div`
    display: flex;
    flex-direction: row;
    flex-wrap: ${({wrap}) => (wrap ? 'wrap' : 'nowrap')};
    gap: 10px;
    align-items: center;
`;

const FlexColumn = styled.div`
    display: flex;
    flex-direction: column;
    gap: 10px;
`;

const DiscountContainer = styled.div`
    display: flex;
    flex-direction: column;
    padding: 5px;
    margin-right: 5px;
    margin-bottom: 5px;
    border-radius: ${GLOBAL_BORDER_RADIUS}px;
    background-color: ${({backgroundColor}) => backgroundColor};
`;

const LoaderWrapper = styled.div`
    position: absolute;
    right: 20px;
    top: 20px;
`;

const StyledDatePicker = styled(DatePicker)`
    ${globalElementInputStyle.customInput};
    border-radius: 4px;
    padding: 8px;
    width: 150px;
`;

const PopupOverlay = styled.div`
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(0, 0, 0, 0.5);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 1000;
`;

const PopupContent = styled.div`
    background-color: ${({backgroundColor}) => backgroundColor};
    padding: 20px;
    border-radius: ${GLOBAL_BORDER_RADIUS}px;
    max-width: 400px;
    width: 90%;
    max-height: 80vh;
    overflow-y: auto;
    position: relative;
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.2);
`;

const CloseButton = styled.button`
    position: absolute;
    top: 10px;
    right: 10px;
    background: none;
    border: none;
    font-size: 24px;
    cursor: pointer;
    color: #333;
`;

export default Profile;
